import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, Lock, Files } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { fetchGeneralSettings, fetchDstSettings } from "@/lib/api";
import {
  calcMonthlyAmortization,
  computeDst,
  daysBetween,
  formatPhp,
  formatPct,
  formatRatePct,
  formatDate,
  addMonths,
  round2,
  lookupAnnualRate,
  computeEIR,
  totalInterest,
} from "@/lib/calc";
import DisclosureStatement from "./DisclosureStatement";
import PnsaPage from "./PnsaPage";
import AmortizationPage from "./AmortizationPage";
import DirectPrintButton from "./DirectPrintButton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import {
  buildDisclosureText,
  buildPnsaText,
  buildSimpleFormText,
  buildAmortizationText,
  openTextPrint,
  downloadEscp,
  buildEscpDocument,
} from "@/lib/dotmatrixText";

const initialForm = (defaults) => ({
  name: "",
  address: "",
  termMonths: defaults?.termMonths ?? 6,
  monthlyRate: defaults?.monthlyRate ?? 0.02,
  loanForBusiness: "No",
  loanAmount: 0,
  loanAmountStr: "",
  loanProduct: "",
  loanAccountNumber: "",
  pnNumber: "",
  email: "",
  prepaymentPenalty: "",
  scheduleType: "installment",
  singlePaymentDate: "",
  collateralJewelry: true,
});

// Fallback minimum loan amount (PHP) if admin setting is unavailable.
const DEFAULT_MIN_LOAN_AMOUNT = 200000;

// Format a numeric/string value into a display string with thousand-separators.
// Preserves an in-progress decimal point and digits the user is typing.
function formatAmountInput(raw) {
  // Strip everything except digits and a single decimal point
  let cleaned = String(raw).replace(/[^0-9.]/g, "");
  const firstDot = cleaned.indexOf(".");
  if (firstDot !== -1) {
    cleaned =
      cleaned.slice(0, firstDot + 1) +
      cleaned.slice(firstDot + 1).replace(/\./g, "");
  }
  const parts = cleaned.split(".");
  const intPart = parts[0] || "";
  const decPart = parts.length > 1 ? parts[1].slice(0, 2) : null;
  const intFormatted =
    intPart === "" ? "" : Number(intPart).toLocaleString("en-PH");
  return decPart !== null ? `${intFormatted}.${decPart}` : intFormatted;
}

function amountStrToNumber(str) {
  const cleaned = String(str).replace(/[^0-9.]/g, "");
  if (cleaned === "" || cleaned === ".") return 0;
  const n = Number(cleaned);
  return isNaN(n) ? 0 : n;
}

export default function Simulator() {
  const [settings, setSettings] = useState({
    default_monthly_rate: 0.02,
    interest_formula: "simple",
    term_options: [6, 7, 8, 9, 10, 11, 12],
    pmt_rates: [],
    field_visibility: {
      show_loan_for_business: false,
      show_number_of_days: false,
      show_maturity_date: false,
      default_loan_for_business: "No",
    },
    allow_interest_rate_edit: true,
  });
  const [form, setForm] = useState(initialForm({ monthlyRate: 0.02 }));
  const [dst, setDst] = useState(null);
  const [today] = useState(new Date());
  const [paperSize, setPaperSize] = useState("letter"); // 'letter' | 'a4' | 'cont8'

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [g, d] = await Promise.all([fetchGeneralSettings(), fetchDstSettings()]);
        if (!active) return;
        setSettings(g);
        setForm((f) => ({
          ...f,
          monthlyRate: g.default_monthly_rate,
          termMonths: g.term_options?.[0] ?? f.termMonths,
          loanForBusiness: g.field_visibility?.default_loan_for_business ?? "No",
        }));
        setDst(d);
      } catch (e) {
        console.error("Failed to load settings", e);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const visibility = settings.field_visibility || {};

  // Switch the print page size between A4/Letter cut sheet and 8" continuous
  // form (for dot-matrix). Injected after the static CSS so it wins the cascade.
  useEffect(() => {
    const id = "dynamic-print-page";
    let el = document.getElementById(id);
    if (!el) {
      el = document.createElement("style");
      el.id = id;
      document.head.appendChild(el);
    }
    const SIZES = {
      letter: { size: "8.5in 11in", margin: "0.5in 0.4in" },
      a4: { size: "210mm 297mm", margin: "12mm 12mm" },
      cont8: { size: "8in 11in", margin: "0.5in 0.4in" },
    };
    const { size, margin } = SIZES[paperSize] || SIZES.letter;
    el.textContent = `@media print { @page { size: ${size}; margin: ${margin}; } }`;
  }, [paperSize]);

  const isPmt = settings.interest_formula === "pmt";
  const lookedUpAnnualRate = useMemo(
    () => lookupAnnualRate(settings.pmt_rates, form.termMonths),
    [settings.pmt_rates, form.termMonths]
  );

  const monthlyAmort = useMemo(
    () =>
      calcMonthlyAmortization({
        formula: settings.interest_formula,
        monthlyRate: form.monthlyRate,
        annualRate: lookedUpAnnualRate,
        termMonths: form.termMonths,
        loanAmount: form.loanAmount,
      }),
    [settings.interest_formula, form.monthlyRate, lookedUpAnnualRate, form.termMonths, form.loanAmount]
  );

  const numberOfDays = useMemo(
    () => daysBetween(today, form.termMonths),
    [today, form.termMonths]
  );

  const dstResult = useMemo(
    () =>
      computeDst({
        dstSettings: dst,
        loanForBusiness: form.loanForBusiness,
        termMonths: form.termMonths,
        loanAmount: form.loanAmount,
        numberOfDays,
      }),
    [dst, form, numberOfDays]
  );

  const netProceeds = round2(Number(form.loanAmount || 0) - Number(dstResult.amount || 0));
  const firstPaymentDate = addMonths(today, 1);
  const maturityDate = addMonths(today, Number(form.termMonths) || 0);

  const interestAmount = useMemo(
    () => totalInterest({ monthlyAmort, termMonths: form.termMonths, loanAmount: form.loanAmount }),
    [monthlyAmort, form.termMonths, form.loanAmount]
  );
  const eir = useMemo(
    () => computeEIR({ termMonths: form.termMonths, monthlyAmort, netProceeds }),
    [form.termMonths, monthlyAmort, netProceeds]
  );

  const upd = (k) => (e) =>
    setForm((f) => ({
      ...f,
      [k]: e.target.type === "number" ? Number(e.target.value) : e.target.value,
    }));

  const updRate = (e) => {
    const v = Number(e.target.value);
    setForm((f) => ({ ...f, monthlyRate: v / 100 }));
  };

  const reset = () =>
    setForm(initialForm({ monthlyRate: settings.default_monthly_rate, termMonths: settings.term_options?.[0] ?? 6 }));

  const MIN_LOAN_AMOUNT = Number(settings.min_loan_amount) > 0
    ? Number(settings.min_loan_amount)
    : DEFAULT_MIN_LOAN_AMOUNT;
  const canExport = Number(form.loanAmount || 0) >= MIN_LOAN_AMOUNT;
  const amountShort = MIN_LOAN_AMOUNT - Number(form.loanAmount || 0);

  const print = () => {
    if (!canExport) {
      toast.error(`Minimum loan amount is ${formatPhp(MIN_LOAN_AMOUNT)} to print.`);
      return;
    }
    const original = document.title;
    const docBase = isDisclosure ? "Disclosure-Statement" : "Loan-Computation";
    // Setting the title controls the default filename when the user picks
    // "Save as PDF" as the print destination.
    document.title = form.name
      ? `${docBase}-${form.name.replace(/[^a-z0-9]+/gi, "-")}`
      : `${docBase}-Sheet`;
    const restore = () => {
      document.title = original;
      window.removeEventListener("afterprint", restore);
    };
    window.addEventListener("afterprint", restore);
    window.print();
  };

  // "Save as PDF" uses the browser's native (vector) print engine — the same
  // crisp, perfectly-aligned output as Print. Users select "Save as PDF" as
  // the destination in the dialog.
  const downloadPdf = print;

  const theme = ["dot_matrix", "disclosure"].includes(settings.theme) ? settings.theme : "modern";
  const isDisclosure = theme === "disclosure";
  const showLogo = settings.show_logo !== false;
  const showPnsa = !!settings.show_pnsa;
  const showAmortization = !!settings.show_amortization;
  // Which pages exist for the current view (used by the reprint page-picker).
  const availablePages = useMemo(() => {
    if (!isDisclosure) return [];
    const list = [{ key: "disclosure", label: "Page 1 \u2014 Disclosure Statement" }];
    if (showPnsa) list.push({ key: "pnsa", label: `Page ${list.length + 1} \u2014 Promissory Note (PNSA)` });
    if (showAmortization) {
      list.push({ key: "amortization", label: `Page ${list.length + 1} \u2014 Amortization Schedule` });
    }
    return list;
  }, [isDisclosure, showPnsa, showAmortization]);
  const [selectedPages, setSelectedPages] = useState({});
  // A page prints unless it was explicitly turned off (default = all on).
  const isPageOn = (key) => selectedPages[key] !== false;
  const togglePage = (key) => {
    setSelectedPages((prev) => {
      const next = { ...prev, [key]: prev[key] === false };
      // Never allow zero pages selected — keep at least this one on.
      const anyOn = availablePages.some((p) => next[p.key] !== false);
      if (!anyOn) return prev;
      return next;
    });
  };
  const selectedCount = availablePages.filter((p) => isPageOn(p.key)).length;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const charges = settings.disclosure_charges || {};

  const contractualRatePct = formatRatePct(form.monthlyRate);
  const eirPct = (eir * 100).toFixed(2);

  // Builds the fixed-width 80-column page(s) for the current view.
  const buildTextPages = () => {
    if (isDisclosure) {
      const disclosure = buildDisclosureText({
        name: form.name,
        address: form.address,
        loanProduct: "JewelFlex Loan",
        loanAccountNumber: form.loanAccountNumber,
        dateGranted: formatDate(today),
        loanAmount: form.loanAmount,
        contractualRatePct,
        fromDate: formatDate(firstPaymentDate),
        toDate: formatDate(maturityDate),
        interestAmount,
        dst: dstResult.amount,
        totalDeductions: dstResult.amount,
        netProceeds,
        eirPct,
        termMonths: form.termMonths,
        monthlyAmort,
        firstInstallmentDate: formatDate(firstPaymentDate),
        charges,
      });
      const pages = [
        { key: "disclosure", label: "Page 1 \u2014 Disclosure Statement", text: disclosure },
      ];
      if (showPnsa) {
        pages.push({
          key: "pnsa",
          label: `Page ${pages.length + 1} \u2014 Promissory Note (PNSA)`,
          text: buildPnsaText({
            pnNumber: form.pnNumber,
            name: form.name,
            address: form.address,
            email: form.email,
            loanAmount: form.loanAmount,
            contractualRatePct,
            interestAmount,
            dst: dstResult.amount,
            netProceeds,
            eirPct,
            termMonths: form.termMonths,
            monthlyAmort,
            dateGranted: formatDate(today),
            firstPaymentDate: formatDate(firstPaymentDate),
          }),
        });
      }
      if (showAmortization) {
        pages.push({
          key: "amortization",
          label: `Page ${pages.length + 1} \u2014 Amortization Schedule`,
          text: buildAmortizationText({
            name: form.name,
            address: form.address,
            loanAmount: form.loanAmount,
            termMonths: form.termMonths,
            annualRate: lookedUpAnnualRate,
            dateGranted: today,
          }),
        });
      }
      return { pages, title: "Disclosure-Statement" };
    }
    return {
      pages: [
        {
          key: "simple",
          label: "Loan Computation",
          text: buildSimpleFormText({
            name: form.name,
            address: form.address,
            termMonths: form.termMonths,
            contractualRatePct,
            loanAmount: form.loanAmount,
            monthlyAmort,
            dst: dstResult.amount,
            netProceeds,
            dateGranted: formatDate(today),
            firstPaymentDate: formatDate(firstPaymentDate),
          }),
        },
      ],
      title: "Loan-Computation",
    };
  };

  const fileBase = (title) =>
    form.name ? `${title}-${form.name.replace(/[^a-z0-9]+/gi, "-")}` : `${title}-Sheet`;

  // Fixed-width plain-text print (opens a clean text window → print dialog).
  const printText = () => {
    if (!canExport) {
      toast.error(`Minimum loan amount is ${formatPhp(MIN_LOAN_AMOUNT)} to print.`);
      return;
    }
    const { pages, title } = buildTextPages();
    const chosen = pages.filter((p) => selectedPages[p.key] !== false);
    openTextPrint(chosen.map((p) => p.text), { title, paper: paperSize });
  };

  // Raw ESC/P file for the EPSON LX-310 — draft mode + 10 cpi pitch, sent
  // straight to the printer for the crispest native-font output.
  const downloadEscpFile = () => {
    if (!canExport) {
      toast.error(`Minimum loan amount is ${formatPhp(MIN_LOAN_AMOUNT)} to print.`);
      return;
    }
    const { pages, title } = buildTextPages();
    const chosen = pages.filter((p) => selectedPages[p.key] !== false);
    downloadEscp(chosen.map((p) => p.text), { filename: fileBase(title), lpi: 6, quality: "lq" });
    toast.success("ESC/P file downloaded (.prn) — Letter Quality, 10 cpi", {
      description:
        "Send it RAW to the LX-310 (a queue set to RAW datatype). Windows: copy /b file.prn \"\\\\localhost\\EPSON LX-310 RAW\" · macOS/Linux: lp -o raw file.prn",
      duration: 11000,
    });
  };

  // Raw ESC/P bytes for the current view — used by direct QZ Tray printing.
  const getEscpBytes = () => {
    if (!canExport) {
      toast.error(`Minimum loan amount is ${formatPhp(MIN_LOAN_AMOUNT)} to print.`);
      return null;
    }
    const { pages } = buildTextPages();
    const chosen = pages.filter((p) => selectedPages[p.key] !== false);
    if (chosen.length === 0) {
      toast.error("Select at least one page to print.");
      return null;
    }
    return buildEscpDocument(chosen.map((p) => p.text), { lpi: 6, quality: "lq" });
  };

  return (
    <div className={`theme-${theme} min-h-screen w-full bg-slate-100 py-8 print:bg-white print:py-0`}>
      {/* Top toolbar */}
      <div className="max-w-4xl mx-auto px-4 mb-4 flex items-center justify-between no-print">
        <Link
          to="/admin/login"
          className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-900 transition-colors"
          data-testid="admin-link"
        >
          <Lock className="h-3 w-3" />
          <span>Admin</span>
        </Link>
        <div className="flex flex-col items-end gap-1">
          {!canExport && (
            <span
              className="text-[11px] text-amber-600 font-medium"
              data-testid="export-min-amount-hint"
            >
              Enter a loan amount of at least {formatPhp(MIN_LOAN_AMOUNT)} to print or save.
            </span>
          )}
          <div className="flex items-center gap-2">
          {isDisclosure && availablePages.length > 1 && (
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2"
                  disabled={!canExport}
                  data-testid="page-picker-button"
                >
                  <Files className="h-4 w-4" />
                  Pages ({selectedCount}/{availablePages.length})
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-72" data-testid="page-picker-panel">
                <p className="text-xs font-semibold text-slate-700 mb-2">Pages to print</p>
                <div className="space-y-0.5">
                  {availablePages.map((p) => (
                    <label
                      key={p.key}
                      className="flex items-center gap-2 py-1.5 px-1 rounded hover:bg-slate-50 cursor-pointer"
                      data-testid={`page-toggle-${p.key}`}
                    >
                      <Checkbox
                        checked={isPageOn(p.key)}
                        onCheckedChange={() => togglePage(p.key)}
                      />
                      <span className="text-sm text-slate-700">{p.label}</span>
                    </label>
                  ))}
                </div>
                <p className="text-[11px] text-slate-400 pt-2 leading-snug">
                  Only ticked pages are sent to the LX-310 — handy for reprinting just one page.
                </p>
              </PopoverContent>
            </Popover>
          )}
          {(isDisclosure || theme === "dot_matrix") && (
            <DirectPrintButton getBytes={getEscpBytes} disabled={!canExport} />
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={reset}
            className="gap-2"
            data-testid="reset-form-button"
          >
            <RotateCcw className="h-4 w-4" />
            Reset
          </Button>
          </div>
        </div>
      </div>

      {/* Printable form */}
      <div
        id="printable-form"
        className="print-page max-w-4xl mx-auto bg-white shadow-xl border border-slate-200 p-10 print:shadow-none print:border-none print:p-0"
        data-testid="simulator-form"
      >
        {isDisclosure ? (
          <>
          <DisclosureStatement
            form={form}
            set={set}
            charges={charges}
            contractualRatePct={contractualRatePct}
            interestAmount={interestAmount}
            dst={dstResult.amount}
            totalDeductions={dstResult.amount}
            netProceeds={netProceeds}
            eirPct={(eir * 100).toFixed(2)}
            monthlyAmort={monthlyAmort}
            dateGranted={today}
            maturityDate={maturityDate}
            firstPaymentDate={firstPaymentDate}
            showLogo={showLogo}
            loanAmountStr={form.loanAmountStr}
            onLoanAmount={(e) => {
              const formatted = formatAmountInput(e.target.value);
              const num = amountStrToNumber(formatted);
              setForm((f) => ({ ...f, loanAmountStr: formatted, loanAmount: num }));
            }}
            termOptions={settings.term_options || []}
          />
          {showPnsa && (
            <PnsaPage
              form={form}
              set={set}
              contractualRatePct={contractualRatePct}
              interestAmount={interestAmount}
              netProceeds={netProceeds}
              eirPct={(eir * 100).toFixed(2)}
              monthlyAmort={monthlyAmort}
              firstPaymentDate={firstPaymentDate}
              loanAmount={form.loanAmount}
              dst={dstResult.amount}
              dateGranted={today}
            />
          )}
          {showAmortization && (
            <AmortizationPage
              name={form.name}
              address={form.address}
              loanAmount={form.loanAmount}
              termMonths={form.termMonths}
              annualRate={lookedUpAnnualRate}
              dateGranted={today}
              showLogo={showLogo}
            />
          )}
          </>
        ) : (
        <>
        {/* Header with logo */}
        <div className="print-shrink-header flex items-center justify-center pb-6 border-b-2 border-slate-900 mb-8 break-inside-avoid">
          {showLogo && (
            <img
              src="/logo.png"
              alt="Company Logo"
              className="print-logo h-28 w-auto object-contain"
              data-testid="company-logo"
            />
          )}
          {/* Plain-text wordmark used by the Dot Matrix theme (the SVG logo
              and the CSS ::after pseudo-element don't render reliably in
              html2canvas, so we use a real element gated by theme CSS). */}
          <span className="dotmatrix-wordmark">Cebuana Lhuillier Bank  - JewelFlex Financing</span>
        </div>

        {/* Borrower Info */}
        <Section title="Borrower Information">
          <Field
            label="Name"
            value={form.name}
            onChange={upd("name")}
            placeholder="Full name"
            testid="name-input"
          />
          <Field
            label="Address"
            value={form.address}
            onChange={upd("address")}
            placeholder="Complete address"
            testid="address-input"
          />
        </Section>

        {/* Loan Details */}
        <Section title="Loan Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <SelectField
              label="Loan Term (in months)"
              value={form.termMonths}
              onChange={upd("termMonths")}
              options={(settings.term_options || [6, 7, 8, 9, 10, 11, 12]).map(String)}
              numeric
              testid="loan-term-select"
            />
            {visibility.show_loan_for_business && (
              <SelectField
                label="Loan for Business?"
                value={form.loanForBusiness}
                onChange={upd("loanForBusiness")}
                options={["Yes", "No"]}
                testid="loan-business-select"
              />
            )}
            {isPmt ? (
              <Computed
                label="Interest Rate (per annum, from PMT table)"
                value={formatPct(lookedUpAnnualRate)}
                testid="computed-interest-rate"
              />
            ) : settings.allow_interest_rate_edit ? (
              <Field
                label="Interest rate (add-on rate per month, %)"
                type="number"
                step="0.01"
                value={(form.monthlyRate * 100).toFixed(2)}
                onChange={updRate}
                testid="interest-rate-input"
              />
            ) : (
              <Computed
                label="Interest rate (add-on rate per month)"
                value={`${(form.monthlyRate * 100).toFixed(2)}%`}
                testid="computed-interest-rate"
              />
            )}
            <Field
              label="Loan Amount (PHP)"
              type="text"
              inputMode="decimal"
              value={form.loanAmountStr}
              onChange={(e) => {
                const formatted = formatAmountInput(e.target.value);
                const num = amountStrToNumber(formatted);
                setForm((f) => ({ ...f, loanAmountStr: formatted, loanAmount: num }));
              }}
              onBlur={() => {
                // Keep empty so the placeholder/guide stays visible
                if (form.loanAmountStr === "") {
                  setForm((f) => ({ ...f, loanAmount: 0 }));
                }
              }}
              prefix="₱"
              placeholder={`e.g. ${MIN_LOAN_AMOUNT.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
              helper={
                Number(form.loanAmount || 0) > 0 && !canExport
                  ? `Minimum is ${formatPhp(MIN_LOAN_AMOUNT)} (₱${amountShort.toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} more needed)`
                  : `Minimum loan amount: ${formatPhp(MIN_LOAN_AMOUNT)}`
              }
              helperError={Number(form.loanAmount || 0) > 0 && !canExport}
              testid="loan-amount-input"
            />
            <Computed
              label="Monthly Amortization"
              value={formatPhp(monthlyAmort)}
              testid="computed-monthly-amortization"
              accent
            />
            <Computed
              label="DST on Promissory Note"
              value={formatPhp(dstResult.amount)}
              testid="computed-dst"
            />
            <Computed
              label="Net Proceeds"
              value={formatPhp(netProceeds)}
              testid="computed-net-proceeds"
              accent
            />
            {visibility.show_number_of_days && (
              <Computed
                label={`Number of Days (${form.termMonths}-mo term)`}
                value={`${numberOfDays} days`}
                testid="computed-number-of-days"
              />
            )}
          </div>
        </Section>

        {/* Dates */}
        <Section title="Schedule">
          <div className={`grid grid-cols-1 ${visibility.show_maturity_date ? "md:grid-cols-3" : "md:grid-cols-2"} gap-x-8`}>
            <Computed label="Date Granted" value={formatDate(today)} testid="computed-date-granted" />
            <Computed
              label="First Payment Date"
              value={formatDate(firstPaymentDate)}
              testid="computed-first-payment"
            />
            {visibility.show_maturity_date && (
              <Computed
                label="Maturity Date"
                value={formatDate(maturityDate)}
                testid="computed-maturity-date"
              />
            )}
          </div>
        </Section>

        {/* Conforme */}
        <div className="print-conforme mt-16 break-inside-avoid">
          <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-2">
            Conforme
          </p>
          <div className="w-2/3">
            {/* Empty space for stamp above the signature line */}
            <div className="print-stamp-area relative h-16 flex justify-end items-end pr-8 pb-1">
              <img
                src="/signature-stamp.png"
                alt="Signature Stamp"
                className="h-16 w-auto object-contain opacity-90 select-none"
                data-testid="signature-stamp"
              />
            </div>
            <div className="signature-line border-t border-slate-900 pt-2">
              <p
                className="font-serif-display text-xl text-slate-900 min-h-[2rem]"
                data-testid="conforme-name"
              >
                {form.name || "\u00A0"}
              </p>
              <p className="text-[10px] tracking-[0.2em] uppercase text-slate-500 mt-1">
                Name of Borrower over Printed Name
              </p>
            </div>
          </div>
        </div>

        {/* Footer note */}
        <p className="print-footer text-[9px] text-slate-400 mt-12 tracking-wide">
          This is a simulation for reference only. Final amounts are subject to verification and approval.
        </p>
        </>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="print-shrink-section mb-6 break-inside-avoid">
      <h2 className="text-[10px] tracking-[0.25em] uppercase text-slate-500 font-semibold pb-1 border-b border-slate-300 mb-3">
        {title}
      </h2>
      {children}
    </section>
  );
}

function Field({ label, value, onChange, onBlur, placeholder, type = "text", step, testid, prefix, inputMode, helper, helperError }) {
  return (
    <div className="form-cell-print py-2 border-b border-slate-300">
      <label className="block text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1">
        {label}
      </label>
      <div className="flex items-baseline gap-1">
        {prefix && (
          <span className="text-base font-medium text-slate-900 tabular-nums select-none">
            {prefix}
          </span>
        )}
        <input
          type={type}
          step={step}
          inputMode={inputMode}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          className="w-full bg-transparent outline-none text-base font-medium text-slate-900 tabular-nums placeholder:text-slate-300 focus:bg-yellow-50"
          data-testid={testid}
        />
      </div>
      {helper && (
        <p
          className={`no-print mt-1 text-[10px] ${helperError ? "text-amber-600 font-medium" : "text-slate-400"}`}
          data-testid={testid ? `${testid}-helper` : undefined}
        >
          {helper}
        </p>
      )}
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid, numeric = false }) {
  return (
    <div className="form-cell-print py-2 border-b border-slate-300">
      <label className="block text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1">
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange({ target: { value: numeric ? Number(e.target.value) : e.target.value, type: numeric ? "number" : "text" } })}
        className="w-full bg-transparent outline-none text-base font-medium text-slate-900 cursor-pointer focus:bg-yellow-50"
        data-testid={testid}
      >
        {options.map((o) => (
          <option key={o} value={o}>
            {o}
          </option>
        ))}
      </select>
    </div>
  );
}

function Computed({ label, value, testid, accent = false }) {
  return (
    <div className="form-cell-print py-2 border-b border-slate-300 break-inside-avoid">
      <label className="block text-[10px] tracking-[0.2em] uppercase text-slate-500 mb-1">
        {label}
      </label>
      <p
        className={`text-base tabular-nums ${
          accent
            ? "font-semibold text-slate-900"
            : "font-medium text-slate-700"
        }`}
        data-testid={testid}
      >
        {value}
      </p>
    </div>
  );
}
